import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Icon from "./Icon.jsx";
import useOutsideClick from "@src/components/utility/OutsideClickHook";
import AppUtils from "@src/utils/appUtils";

const DropdownTemplate = (props) => {
  const {
    value,
    onChange,
    options,
    minWidth,
    className,
    disabled = false,
    fullWidth,
    enableSearch = false,
    errorState,
  } = props;
  const [open, setOpen] = useState(false);
  const [longestOptionLength, setLongestOptionLength] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const optionsToDisplay = [];
  let selectedOption = options.filter((option) =>
    option.value == null
      ? option.value === value
      : option.value?.toString() === value?.toString(),
  )[0];
  if (!selectedOption) {
    selectedOption = options[0];
  }
  const valueToDisplay = selectedOption.text ? selectedOption.text : value;
  const selectedColour = selectedOption.colour ? selectedOption.colour : null;

  // Notify parent when the current value doesn't match any option so the
  // parent can sync its state. Must be an effect — calling onChange during
  // render causes a "setState while rendering" React error.
  useEffect(() => {
    const hasMatch = options.some((option) =>
      option.value == null
        ? option.value === value
        : option.value?.toString() === value?.toString(),
    );
    if (!hasMatch && options.length > 0) {
      onChange(options[0].value);
    }
  }, [value, options]);

  //work out length of longest option so we can set the width of the dropdown to accomodate it when it isnt the selected option
  useEffect(() => {
    // use the previous value so the dropdown doesnt get smaller when the options change
    let result = longestOptionLength;
    options.forEach((option) => {
      result = option.text?.length > result ? option.text?.length : result;
    });
    setLongestOptionLength(result);
  }, [options]);

  // clear search when dropdown opens so user always starts with full list
  useEffect(() => {
    if (open) setSearchTerm("");
  }, [open]);

  const handleClickOutside = () => {
    setOpen(false);
  };

  const ref = useOutsideClick(handleClickOutside);

  const handleOnChange = (event) => {
    const newValue = event.currentTarget.getAttribute("data-value");
    onChange(newValue);
    setOpen(false);
    setSearchTerm("");
  };

  // filter options by search term (case-insensitive)
  const filteredOptions = options.filter((option) => {
    const needle = searchTerm?.toString().toLowerCase() || "";
    const hay = (option.text ?? option.value ?? "").toString().toLowerCase();
    return hay.includes(needle);
  });

  // Build display items from filteredOptions
  filteredOptions.forEach((option, index) => {
    const isSelected =
      option.value == null
        ? option.value === value
        : option.value?.toString() === value?.toString();
    optionsToDisplay.push(
      <div
        key={`dropdown-item-${index}`}
        className={`px-4 py-2 hover:bg-neutral-100 cursor-pointer block w-full text-left ${
          isSelected ? " font-bold" : ""
        }`}
        onClick={handleOnChange}
        data-value={option.value}
      >
        {option.colour && (
          <div
            className={`w-3 h-3 flex-shrink-0 bg-${option.colour}-500 rounded-full inline-block mr-2`}
          />
        )}

        {/* Option text */}
        <span>{option.text || option.value}</span>
      </div>,
    );
  });
  // if no results, show a friendly message
  if (filteredOptions.length === 0) {
    optionsToDisplay.push(
      <div
        key={`dropdown-item-empty`}
        className={`px-4 py-2 text-neutral-500 block w-full text-left`}
      >
        No results
      </div>,
    );
  }
  return (
    <div
      ref={ref}
      className={`h-10 text-${errorState ? "red-400" : "black"} ${minWidth ? " min-w-64" : fullWidth ? "w-full" : ""} relative ${className}`}
    >
      <div
        onClick={() => {
          if (!disabled) {
            return setOpen(!open);
          }
        }}
        className={`flex h-full items-center justify-between px-4 cursor-pointer border border-${errorState ? "red" : "neutral"}-200 transition-all rounded-lg ${
          open ? "border-b-0 rounded-b-none" : ""
        } ${disabled ? "bg-neutral-200 cursor-not-allowed" : "bg-white"}`}
      >
        <div className="flex-1 flex overflow-hidden items-center">
          {selectedColour && (
            <div
              className={`w-3 h-3 flex-shrink-0 bg-${selectedColour}-500 rounded-full inline-block mr-2`}
            />
          )}
          <span
            className="block truncate select-none"
            style={{
              maxWidth: "calc(100% - 1.5rem)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "left",
            }}
          >
            {valueToDisplay}
          </span>
        </div>
        <Icon
          textColor={
            disabled ? "neutral-400" : errorState ? "red-400" : "black"
          }
          name="DropDownChevron"
          backgroundColor={disabled ? "neutral-200" : "white"}
          size="4"
          padding={false}
          centered={false}
          className={`transition-all ${open ? "transform rotate-180" : ""}`}
        />
      </div>
      <div
        className={`flex flex-col items-center absolute w-full bg-white rounded-b transition-all ${
          open
            ? `border border-${errorState ? "red" : "neutral"}-200 border-t-0`
            : ""
        }`}
        style={{ zIndex: 99999 }}
      >
        <div
          className={
            open
              ? `m-3 border-b-2 border-b-${errorState ? "red" : "neutral"}-100 w-11/12`
              : ""
          }
        ></div>
        {/* Search input shown when dropdown is open */}
        {open && enableSearch && (
          <div className="w-11/12 mb-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 border rounded text-sm"
            />
          </div>
        )}
        <div
          className={`w-full transition-all ${"overflow-y-auto"}`}
          style={{
            height: open
              ? options.length > 4
                ? "10.5rem"
                : `${options.length * 2.5 + 0.5}rem`
              : "0rem",
            maxHeight: open ? "10.5rem" : "0rem",
          }}
        >
          <div className="w-full">{optionsToDisplay}</div>
        </div>
      </div>
    </div>
  );
};

const DropDown = (props) => {
  const {
    value,
    onChange,
    options,
    minWidth = true,
    className = "",
    fullWidth,
    enableSearch,
    errorState,
  } = props;
  if (!options || options.length === 0)
    return (
      <DropdownTemplate
        fullWidth={fullWidth}
        minWidth={minWidth}
        className={className}
        value={""}
        onChange={onChange}
        options={[{ value: "", text: "" }]}
        disabled={true}
        errorState={errorState}
      />
    );

  return (
    <DropdownTemplate
      fullWidth={fullWidth}
      minWidth={minWidth}
      className={className}
      value={value}
      onChange={onChange}
      options={options}
      enableSearch={enableSearch}
      errorState={errorState}
    />
  );
};

export const dropDownControls = {
  value: {
    propType: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.bool,
      PropTypes.string,
    ]).isRequired,
    description: "Currently selected value",
    controlType: "text",
    defaultValue: "",
  },
  onChange: {
    propType: PropTypes.func.isRequired,
    description: "Selection change handler",
    disable: true,
  },
  options: {
    propType: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.bool,
          PropTypes.string,
        ]).isRequired,
        text: PropTypes.string.isRequired,
      }),
    ).isRequired,
    description: "Available dropdown options",
    controlType: "object",
    defaultValue: [
      { value: "EAEU", text: "Efficient Auxiliary Engine Use" },
      { value: "ESFOC", text: "Efficient SFOC" },
    ],
  },
};

DropDown.propTypes = AppUtils.getPropTypeFromControls(dropDownControls);

export default DropDown;
